import { motion } from 'framer-motion';
import {
  RadarChart, PolarGrid, PolarAngleAxis, Radar,
  PieChart, Pie, Cell,
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer,
} from 'recharts';
import { BarChart3, TrendingUp, Zap } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';

/* ─── Grade colour helpers ─────────────────────────────────────────────── */
export const GRADE_COLORS = {
  'A+': 'text-emerald-500', A: 'text-emerald-500',
  B: 'text-green-500', C: 'text-amber-500',
  D: 'text-orange-500', F: 'text-red-500',
};
const GRADE_HEX = {
  'A+': '#10b981', A: '#10b981', B: '#22c55e',
  C: '#f59e0b', D: '#f97316', F: '#ef4444',
};

/* ─── 1. ScoreGauge ─────────────────────────────────────────────────────── */
export const ScoreGauge = ({ score = 0, grade = 'C' }) => {
  const W = 220, H = 140;
  const strokeW = 18;
  const cx = W / 2;
  const cy = H - 15;
  const r = 85;

  const color = GRADE_HEX[grade] || '#94a3b8';

  // stroke-dasharray on a <circle> — the most reliable semicircle gauge technique.
  // A circle's stroke starts at 3-o'clock (right). rotate(180) moves start to 9-o'clock (left).
  // halfCirc = exactly the top semicircle length.
  const fullCirc = 2 * Math.PI * r;
  const halfCirc = Math.PI * r;
  const filled = (score / 100) * halfCirc;

  // Needle endpoint — standard math angles, y inverted for SVG
  const needleDeg = 180 - (score / 100) * 180;
  const needleRad = (needleDeg * Math.PI) / 180;
  const needleLen = r - 22;
  const nx = cx + needleLen * Math.cos(needleRad);
  const ny = cy - needleLen * Math.sin(needleRad);

  return (
    <div className="flex flex-col items-center">
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
        {/* Background track — full semicircle */}
        <circle
          cx={cx} cy={cy} r={r}
          fill="none"
          stroke="#e2e8f0"
          strokeWidth={strokeW}
          strokeDasharray={`${halfCirc} ${fullCirc}`}
          transform={`rotate(180 ${cx} ${cy})`}
          strokeLinecap="round"
        />

        {/* Score arc — partial fill */}
        <circle
          cx={cx} cy={cy} r={r}
          fill="none"
          stroke={color}
          strokeWidth={strokeW}
          strokeDasharray={`${filled} ${fullCirc}`}
          transform={`rotate(180 ${cx} ${cy})`}
          strokeLinecap="round"
          style={{
            filter: `drop-shadow(0 0 6px ${color}55)`,
            transition: 'stroke-dasharray 1s ease-out',
          }}
        />

        {/* Tick marks at 0%, 25%, 50%, 75%, 100% */}
        {[0, 25, 50, 75, 100].map((pct) => {
          const deg = 180 - (pct / 100) * 180;
          const rad = (deg * Math.PI) / 180;
          const ix = cx + (r - 24) * Math.cos(rad);
          const iy = cy - (r - 24) * Math.sin(rad);
          const ox = cx + (r + 6) * Math.cos(rad);
          const oy = cy - (r + 6) * Math.sin(rad);
          return (
            <line key={pct} x1={ix} y1={iy} x2={ox} y2={oy}
              stroke="#cbd5e1" strokeWidth="1.5" />
          );
        })}

        {/* Needle */}
        <line
          x1={cx} y1={cy} x2={nx} y2={ny}
          stroke={color} strokeWidth="3" strokeLinecap="round"
        />

        {/* Center pivot */}
        <circle cx={cx} cy={cy} r="8" fill={color} />
        <circle cx={cx} cy={cy} r="4" fill="white" />
      </svg>

      <div className="flex flex-col items-center -mt-3 gap-1">
        <span className={`text-4xl font-bold font-heading ${GRADE_COLORS[grade] || 'text-slate-600'}`}>
          {score}
          <span className="text-lg text-slate-400 font-normal">/100</span>
        </span>
        <Badge variant="outline" className={`font-bold text-sm px-3 ${GRADE_COLORS[grade]}`}>
          Grade {grade}
        </Badge>
      </div>
    </div>
  );
};

/* ─── 2. SEORadarChart ──────────────────────────────────────────────────── */
const RADAR_COLORS = { fill: '#6366f1', stroke: '#6366f1' };

const shortenLabel = (label) => {
  const map = {
    'Title Tag': 'Title', 'Meta Description': 'Meta Desc',
    'Header Structure': 'Headers', 'Content Quality': 'Content',
    'Keyword Optimization': 'Keywords', 'Internal/External Links': 'Links',
    'Image Optimization': 'Images', 'Page-Level Technical': 'Technical',
  };
  return map[label] || label.slice(0, 11);
};

export const SEORadarChart = ({ sections = {} }) => {
  const data = Object.entries(sections).map(([key, val]) => ({
    subject: shortenLabel(val?.label || key.replace(/_/g, ' ')),
    value: Math.round(((val?.score ?? 0) / (val?.max ?? 10)) * 100),
    fullMark: 100,
  }));

  if (data.length === 0) return null;

  return (
    <ResponsiveContainer width="100%" height={240}>
      <RadarChart cx="50%" cy="50%" outerRadius="70%" data={data}>
        <PolarGrid stroke="#e2e8f0" className="dark:[stroke:#334155]" />
        <PolarAngleAxis
          dataKey="subject"
          tick={{ fontSize: 11, fill: '#64748b' }}
        />
        <Radar
          name="SEO Score"
          dataKey="value"
          stroke={RADAR_COLORS.stroke}
          fill={RADAR_COLORS.fill}
          fillOpacity={0.3}
          strokeWidth={2}
          dot={{ r: 3, fill: RADAR_COLORS.stroke }}
        />
        <RechartsTooltip
          formatter={(val) => [`${val}/100`, 'Score']}
          contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
        />
      </RadarChart>
    </ResponsiveContainer>
  );
};

/* ─── 3. ScoreTimeline ──────────────────────────────────────────────────── */
export const ScoreTimeline = ({ scans = [] }) => {
  if (scans.length < 2) return null;

  const data = scans
    .slice()
    .reverse()
    .map((s) => ({
      date: new Date(s.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      score: s.audit_scores?.overall_score ?? 0,
      grade: s.audit_scores?.grade ?? '?',
    }));

  return (
    <ResponsiveContainer width="100%" height={80}>
      <AreaChart data={data} margin={{ top: 4, right: 8, left: -32, bottom: 0 }}>
        <defs>
          <linearGradient id="scoreGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#6366f1" stopOpacity={0.35} />
            <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
        <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} />
        <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#94a3b8' }} />
        <RechartsTooltip
          formatter={(val, _, props) => [`${val}/100 (${props.payload?.grade})`, 'Score']}
          contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
        />
        <Area
          type="monotone"
          dataKey="score"
          stroke="#6366f1"
          strokeWidth={2}
          fill="url(#scoreGrad)"
          dot={{ r: 3, fill: '#6366f1' }}
          activeDot={{ r: 5 }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

/* ─── 4. AuditScoreCard (hero) ──────────────────────────────────────────── */
const ASSESSMENT = (score) => {
  if (score >= 90) return 'Excellent — maintain this standard and focus on edge cases.';
  if (score >= 80) return 'Strong foundation — tackle the remaining gaps to reach excellence.';
  if (score >= 70) return 'Good start — addressing highlighted sections will boost rankings.';
  if (score >= 60) return 'Average — several important optimizations are needed.';
  if (score >= 50) return 'Below average — prioritise critical issues immediately.';
  return 'Needs work — follow the recommendations below to improve significantly.';
};

export const AuditScoreCard = ({ auditScores, scans = [] }) => {
  if (!auditScores) return null;
  const { overall_score = 0, grade = 'C', sections = {} } = auditScores;

  return (
    <Card className="gds-card overflow-hidden">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-indigo-600" />
          SEO Health Score
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col md:flex-row gap-6 items-center md:items-start">
          {/* Left: animated gauge */}
          <div className="flex-shrink-0">
            <ScoreGauge score={overall_score} grade={grade} />
          </div>

          {/* Center: radar chart */}
          <div className="flex-1 w-full min-w-0">
            <p className="text-xs font-medium text-slate-500 uppercase mb-1 text-center">Section Radar</p>
            <SEORadarChart sections={sections} />
          </div>

          {/* Right: summary */}
          <div className="flex-shrink-0 flex flex-col items-center md:items-end gap-3 text-center md:text-right max-w-[180px]">
            <div className="space-y-1">
              <p className="text-xs uppercase text-slate-400 font-medium tracking-wide">Overall</p>
              <p className={`text-5xl font-bold font-heading ${GRADE_COLORS[grade] || 'text-slate-600'}`}>{overall_score}</p>
              <p className="text-sm text-slate-500">out of 100</p>
            </div>
            <Badge variant="outline" className={`text-base px-4 py-1 font-bold ${GRADE_COLORS[grade]}`}>
              Grade {grade}
            </Badge>
            <p className="text-xs text-slate-500 leading-relaxed">{ASSESSMENT(overall_score)}</p>
          </div>
        </div>

        {/* Timeline sparkline */}
        {scans.length > 1 && (
          <div className="mt-5 pt-4 border-t border-slate-100 dark:border-slate-800">
            <p className="text-xs font-medium text-slate-500 uppercase mb-2 flex items-center gap-1">
              <TrendingUp className="w-3.5 h-3.5" /> Score Trend
            </p>
            <ScoreTimeline scans={scans} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/* ─── 5. CategoryDonut ──────────────────────────────────────────────────── */
const CAT_COLORS = {
  meta_tags: '#8b5cf6', headings: '#3b82f6', content: '#10b981',
  images: '#f59e0b', links: '#06b6d4', technical: '#64748b', general: '#94a3b8',
};
const CAT_LABELS = {
  meta_tags: 'Meta Tags', headings: 'Headings', content: 'Content',
  images: 'Images', links: 'Links', technical: 'Technical', general: 'General',
};

const DonutCenterLabel = ({ cx, cy, total }) => (
  <>
    <text x={cx} y={cy - 6} textAnchor="middle" dominantBaseline="middle"
      className="fill-slate-800 dark:fill-slate-200" style={{ fontSize: 20, fontWeight: 700 }}>
      {total}
    </text>
    <text x={cx} y={cy + 14} textAnchor="middle" dominantBaseline="middle"
      style={{ fontSize: 11, fill: '#94a3b8' }}>
      issues
    </text>
  </>
);

export const CategoryDonut = ({ recommendations = [] }) => {
  const counts = recommendations.reduce((acc, r) => {
    const cat = r.category || 'general';
    acc[cat] = (acc[cat] || 0) + 1;
    return acc;
  }, {});

  const data = Object.entries(counts).map(([cat, count]) => ({
    name: CAT_LABELS[cat] || cat,
    value: count,
    color: CAT_COLORS[cat] || '#94a3b8',
  }));

  const total = recommendations.length;

  return (
    <Card className="gds-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading text-base flex items-center gap-2">
          <Zap className="w-4 h-4 text-violet-500" />
          Issues by Category
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-center gap-4">
          <PieChart width={200} height={200}>
            <Pie
              data={data}
              cx={100} cy={100}
              innerRadius={55} outerRadius={85}
              paddingAngle={3}
              dataKey="value"
            >
              {data.map((entry, i) => (
                <Cell key={i} fill={entry.color} />
              ))}
            </Pie>
            <RechartsTooltip
              formatter={(val, name) => [val, name]}
              contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
            />
            <DonutCenterLabel cx={100} cy={100} total={total} />
          </PieChart>

          {/* Legend */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 w-full">
            {data.map((entry) => (
              <div key={entry.name} className="flex items-center gap-2 text-xs">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: entry.color }} />
                <span className="text-slate-600 dark:text-slate-400 truncate">{entry.name}</span>
                <span className="ml-auto font-medium tabular-nums">{entry.value}</span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/* ─── 6. ImpactEffortMatrix ─────────────────────────────────────────────── */
const EFFORT_LEVELS = ['low', 'medium', 'high'];
const SEVERITY_LEVELS = ['high', 'medium', 'low'];

export const ImpactEffortMatrix = ({ recommendations = [] }) => {
  // Build 3x3 count matrix [severity][effort]
  const matrix = {};
  SEVERITY_LEVELS.forEach((sev) => {
    matrix[sev] = {};
    EFFORT_LEVELS.forEach((eff) => { matrix[sev][eff] = 0; });
  });
  recommendations.forEach((r) => {
    const sev = r.severity || 'medium';
    const eff = r.effort || 'medium';
    if (matrix[sev] && matrix[sev][eff] !== undefined) matrix[sev][eff]++;
  });

  const cellBg = (sev, eff) => {
    const count = matrix[sev]?.[eff] || 0;
    if (count === 0) return 'bg-slate-50 dark:bg-slate-800/40 text-slate-300';
    if (sev === 'high' && eff === 'low') return 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 ring-2 ring-emerald-400 dark:ring-emerald-600';
    if (sev === 'high') return 'bg-red-50 dark:bg-red-900/20 text-red-600';
    if (sev === 'medium') return 'bg-amber-50 dark:bg-amber-900/20 text-amber-700';
    return 'bg-blue-50 dark:bg-blue-900/20 text-blue-600';
  };

  return (
    <Card className="gds-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading text-base flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-indigo-500" />
          Impact / Effort Matrix
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {/* Column headers */}
          <div className="grid grid-cols-4 gap-1 text-xs text-center font-medium text-slate-500">
            <div />
            {EFFORT_LEVELS.map((e) => (
              <div key={e} className="capitalize">{e} effort</div>
            ))}
          </div>

          {SEVERITY_LEVELS.map((sev) => (
            <div key={sev} className="grid grid-cols-4 gap-1 items-center text-xs">
              <div className="capitalize font-medium text-slate-600 dark:text-slate-400 pr-1 text-right">
                {sev} sev.
              </div>
              {EFFORT_LEVELS.map((eff) => {
                const count = matrix[sev]?.[eff] || 0;
                const isQuickWin = sev === 'high' && eff === 'low';
                return (
                  <div
                    key={eff}
                    className={`rounded-lg p-2 text-center font-bold text-base relative ${cellBg(sev, eff)}`}
                  >
                    {count}
                    {isQuickWin && count > 0 && (
                      <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[9px] bg-emerald-500 text-white rounded-full px-1.5 whitespace-nowrap font-semibold">
                        Quick Win
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}

          <p className="text-xs text-slate-400 text-center pt-1">
            High severity + low effort = Quick Wins
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
